import { ChangeContext, NgxSliderModule, Options, SliderComponent } from '@angular-slider/ngx-slider';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  Output,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  input
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NumericPipesModule, ToDecimalPipe } from '@partners/utils';
import { Observable, tap } from 'rxjs';

@Component({
  selector: 'numeric-slider',
  standalone: true,
  imports: [NgxSliderModule, NumericPipesModule],
  template: `
    <ngx-slider #slider [value]="value()" [options]="options()"></ngx-slider>
  `,
  styleUrl: 'numeric-slider.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hide-tick-labels]': 'hideTickLabels()'
  },
  styles: [
    `
      .mat-mdc-form-field-subscript-wrapper {
        height: 0;
        min-height: 0;
      }
      .hide-tick-labels {
        .ngx-slider-bubble.ngx-slider-limit {
          visibility: hidden !important;
        }
      }
    `
  ]
})
export class NumericSliderComponent implements AfterViewInit {
  #moneyPipe = inject(ToDecimalPipe);

  step = input<number>(1);
  minValue = input<number>(0);
  maxValue = input<number>(0);
  // thumbLabel = input<boolean>(true); /** wether to show a floating label with current value above the knob */
  showTicks = input<boolean>(false);
  disabled = input<boolean>(false);
  value = input(0);
  debounce = input<number>(200);
  showLabels = input<boolean>(false);

  /**
   * the slider can emit change event in 2 ways: (1) valueChange will emit on every change
   * (2) userChange will emit only via user interaction.. the difference between the two is
   * that the former emits both on user interaction and when the slider's value is set via
   * code, while the latter will fire ONLY when USER interact with the slider.
   *
   * it is helpful to separate those events in case we want the slider to emit change event
   * when the user interact with it AND to respond to changes made TO IT as side effect by
   * other setters. in such scenario - binding to valueChange will cause the slider to get
   * caught in infinite loop of change events. using the userChange however help us avoid
   * these side-effects
   */
  changeEmitter = input<'user-only' | 'user-slider'>('user-only');

  @Output()
  valueChange = new EventEmitter<number>();

  @Output()
  valueChangeEnd = new EventEmitter<number>();

  @ViewChild('slider', { read: SliderComponent })
  sliderInstance: SliderComponent;

  hideTickLabels = input<boolean>(false);

  options = computed<Options>(() => {
    return {
      disabled: this.disabled(),
      floor: this.minValue(),
      ceil: this.maxValue(),
      step: this.step(),
      showTicks: this.showTicks(),
      showSelectionBar: true,
      hidePointerLabels: true,
      showTicksValues: false,
      showSelectionBarFromValue: 0,
      translate: (value: number): string => {
        return this.#moneyPipe.transform(value);
      }
    };
  });

  @HostBinding('class.numeric-slider')
  hostClass = true;

  #destroyRef = inject(DestroyRef);

  ngAfterViewInit(): void {
    this.#bindChangeEvent();
  }

  /**
   * binds a local change emitter to the slider's change event/s. there are two optional
   * change event triggers: user and default. the difference is in the initiator. in user
   * mode change will trigger only while the user interact with the slider. in default mode
   * the change will trigger via user interaction AND setting the slider's value via code
   * (see local changeEmitter output comment above for further details)
   * @returns nothing
   */
  #bindChangeEvent(): void {
    const { userChange, valueChange } = this.sliderInstance;

    if (this.changeEmitter() === 'user-only') {
      this.fromChangeEvent<ChangeContext>(userChange)
        .pipe(
          tap(({ value }) => {
            this.valueChange.emit(value);
          })
        )
        .subscribe();

      return;
    }

    this.fromChangeEvent<number>(valueChange)
      .pipe(
        tap((value) => {
          this.valueChange.emit(value);
        })
      )
      .subscribe();
  }

  fromChangeEvent<T>(changeEmitter: EventEmitter<T>): Observable<T> {
    return changeEmitter.asObservable().pipe(
      takeUntilDestroyed(this.#destroyRef)
      // throttleTime(this.debounce(), undefined, { leading: true, trailing: true })
    );
  }

  ngOnDestroy(): void {
    console.log('Method not implemented.');
  }
}
